import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ThumbsUp, ThumbsDown, Package, Calendar, Star, Plus, X, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import ReCAPTCHA from 'react-google-recaptcha';
import API from '@/utils/api';
import { uploadToCloudinary } from '@/utils/cloudinary';
import { useAuth } from '@/contexts/AuthContext';
import ListingCard from '@/components/ListingCard';

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

export default function SellerProfile() {
  const params = useParams();
  // Support both /sellers/:id and /:username routes
  const id = params.id || params.username;
  const { user } = useAuth();
  const [seller, setSeller] = useState(null);
  const [listings, setListings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewModal, setReviewModal] = useState(false);
  const [review, setReview] = useState({ rating_type: 'positive', text: '', proof_image_url: '', captcha_token: '' });
  const [reviewLoading, setReviewLoading] = useState(false);
  const [proofLoading, setProofLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const captchaRef = useRef();
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        // id can be a UUID or a username - backend handles both
        const [s, l, r] = await Promise.all([
          API.get(`/sellers/${id}`),
          API.get(`/sellers/${id}/listings`),
          API.get(`/sellers/${id}/reviews`),
        ]);
        setSeller(s.data);
        setListings(l.data.listings || []);
        setReviews(r.data.reviews || []);
      } catch { toast.error('Seller not found'); }
      finally { setLoading(false); }
    };
    load();
  }, [id]);

  const handleProofUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProofLoading(true);
    try {
      const url = await uploadToCloudinary(file, 'reviews');
      setReview(r => ({ ...r, proof_image_url: url }));
      toast.success('Proof uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setProofLoading(false); }
  };

  const handleReview = async (e) => {
    e.preventDefault();
    if (!captchaToken) return toast.error('Please complete the reCAPTCHA');
    if (!review.text.trim()) return toast.error('Review text required');
    if (!review.proof_image_url) return toast.error('Proof screenshot required');
    setReviewLoading(true);
    try {
      const { data } = await API.post('/reviews', { ...review, seller_id: id, captcha_token: captchaToken });
      setReviews(prev => [data, ...prev]);
      setSeller(s => ({ ...s, total_reviews: s.total_reviews + 1 }));
      toast.success('Review submitted!');
      setReviewModal(false);
      setReview({ rating_type: 'positive', text: '', proof_image_url: '', captcha_token: '' });
      captchaRef.current?.reset();
      setCaptchaToken('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
      captchaRef.current?.reset();
      setCaptchaToken('');
    } finally { setReviewLoading(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-rm-red border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!seller) return null;

  const joinDate = seller.joined_at ? new Date(seller.joined_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long' }) : 'Unknown';

  return (
    <div className="min-h-screen bg-[#080808]">
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 animate-fade-in">
        {/* Profile Header */}
        <div className="bg-[#0d0d0d] border border-[#1e1e1e] rounded-sm p-6 md:p-8 mb-8 relative overflow-hidden" data-testid="seller-profile">
          {/* Subtle top accent */}
          <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-rm-red/30 to-transparent" />

          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            {/* Large Avatar */}
            <div className="relative shrink-0">
              <div className="w-20 h-20 bg-rm-red/10 border border-rm-red/30 rounded-sm flex items-center justify-center">
                <span className="font-rajdhani font-bold text-4xl text-rm-red">{seller.username?.[0]?.toUpperCase()}</span>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="font-rajdhani font-bold text-3xl md:text-4xl uppercase tracking-tight" data-testid="seller-username">
                {seller.username}
              </h1>
              <div className="flex flex-wrap items-center gap-4 mt-2">
                <div className="flex items-center gap-1.5 text-xs text-zinc-600">
                  <Calendar size={11} /> Joined {joinDate}
                </div>
                <div className="flex items-center gap-1.5 text-xs text-zinc-600">
                  <Package size={11} /> {seller.account_age_days} days active
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="flex gap-1 shrink-0">
              {[
                { val: `${seller.positive_percent}%`, label: 'Positive', color: 'text-green-500' },
                { val: seller.total_reviews, label: 'Reviews', color: 'text-white' },
                { val: seller.active_listings, label: 'Listings', color: 'text-white' },
              ].map(({ val, label, color }) => (
                <div key={label} className="stat-card text-center min-w-[80px]">
                  <p className={`font-rajdhani font-bold text-2xl ${color}`}>{val}</p>
                  <p className="text-[9px] text-zinc-600 uppercase tracking-[0.12em] mt-0.5">{label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Rating bar */}
          {seller.total_reviews > 0 && (
            <div className="mt-6 flex items-center gap-4">
              <div className="flex-1 rating-bar">
                <div className="rating-fill" style={{ width: `${seller.positive_percent}%` }} />
              </div>
              <span className="text-xs text-zinc-600 shrink-0">{seller.positive_reviews}+ / {seller.negative_reviews}-</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Listings */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-6">
              <h2 className="font-rajdhani font-bold text-2xl uppercase tracking-tight">
                Active <span className="text-rm-red">Listings</span>
              </h2>
              <span className="text-sm text-zinc-600">({seller.active_listings})</span>
            </div>
            {listings.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {listings.map(l => <ListingCard key={l.id} listing={l} />)}
              </div>
            ) : (
              <div className="bg-[#0d0d0d] border border-[#1e1e1e] rounded-sm p-12 text-center">
                <Package size={32} className="mx-auto mb-3 text-zinc-800" />
                <p className="text-sm text-zinc-600">No active listings</p>
              </div>
            )}
          </div>

          {/* Reviews */}
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-rajdhani font-bold text-2xl uppercase tracking-tight">Reviews</h2>
              {user && user.id !== id && user.role === 'buyer' && (
                <button onClick={() => setReviewModal(true)} data-testid="leave-review-btn"
                  className="flex items-center gap-1.5 btn-primary px-3 py-2 text-xs rounded-sm">
                  <Plus size={11} /> Review
                </button>
              )}
            </div>
            <div className="space-y-3">
              {reviews.length === 0 ? (
                <div className="bg-[#0d0d0d] border border-[#1e1e1e] rounded-sm p-8 text-center">
                  <Star size={24} className="mx-auto mb-3 text-zinc-800" />
                  <p className="text-sm text-zinc-600">No reviews yet</p>
                </div>
              ) : reviews.map(r => (
                <div key={r.id} className="bg-[#0d0d0d] border border-[#1e1e1e] rounded-sm p-4 animate-fade-in hover:border-[#252525] transition-colors" data-testid={`review-${r.id}`}>
                  <div className="flex items-center justify-between mb-2.5">
                    <span className="font-rajdhani font-bold text-sm text-white">{r.buyer_username}</span>
                    {r.rating_type === 'positive'
                      ? <div className="flex items-center gap-1 text-green-500 text-[11px] bg-green-500/5 border border-green-500/20 px-2 py-0.5 rounded-sm"><ThumbsUp size={10} /> Positive</div>
                      : <div className="flex items-center gap-1 text-rm-red text-[11px] bg-rm-red/5 border border-rm-red/20 px-2 py-0.5 rounded-sm"><ThumbsDown size={10} /> Negative</div>}
                  </div>
                  <p className="text-xs text-zinc-500 mb-3 leading-relaxed">{r.text}</p>
                  {r.proof_image_url && (
                    <button onClick={() => setLightbox(r.proof_image_url)} data-testid={`review-proof-${r.id}`}
                      className="block w-full aspect-video bg-[#111] rounded-sm overflow-hidden border border-[#222] hover:border-rm-red/30 transition-colors">
                      <img src={r.proof_image_url} alt="Proof" className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity" />
                    </button>
                  )}
                  <p className="text-[10px] text-zinc-700 mt-2">{new Date(r.created_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Review Modal */}
      {reviewModal && (
        <div className="fixed inset-0 modal-backdrop z-50 flex items-center justify-center p-4" data-testid="review-modal">
          <div className="bg-[#0f0f0f] border border-[#252525] rounded-sm max-w-md w-full p-6 animate-scale-in shadow-[0_25px_60px_rgba(0,0,0,0.8)] max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-rajdhani font-bold text-xl uppercase tracking-wide">Leave a Review</h3>
              <button onClick={() => setReviewModal(false)} className="w-8 h-8 flex items-center justify-center text-zinc-600 hover:text-white hover:bg-white/5 rounded-sm transition-colors">
                <X size={16} />
              </button>
            </div>
            <form onSubmit={handleReview} className="space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600 block mb-2.5">Rating</label>
                <div className="grid grid-cols-2 gap-3">
                  {['positive', 'negative'].map(rt => (
                    <button key={rt} type="button" onClick={() => setReview(r => ({ ...r, rating_type: rt }))}
                      data-testid={`rating-${rt}`}
                      className={`py-3 border rounded-sm font-rajdhani font-bold uppercase text-sm flex items-center justify-center gap-2 transition-all
                        ${review.rating_type === rt
                          ? rt === 'positive'
                            ? 'border-green-500/60 text-green-500 bg-green-500/8'
                            : 'border-rm-red/60 text-rm-red bg-rm-red/8'
                          : 'border-[#222] text-zinc-600 hover:border-[#333] hover:text-zinc-400'}`}>
                      {rt === 'positive' ? <><ThumbsUp size={13} /> Positive</> : <><ThumbsDown size={13} /> Negative</>}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600 block mb-2.5">Review Text *</label>
                <textarea value={review.text} onChange={e => setReview(r => ({ ...r, text: e.target.value }))}
                  placeholder="Describe your experience with this seller..." rows={3} data-testid="review-text"
                  className="w-full bg-[#0a0a0a] border border-[#222] text-white placeholder:text-zinc-700 px-3 py-2.5 rounded-sm text-sm resize-none" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600 block mb-2.5">Proof Screenshot *</label>
                {review.proof_image_url ? (
                  <div className="relative">
                    <img src={review.proof_image_url} alt="Proof" className="w-full rounded-sm border border-[#222]" />
                    <button type="button" onClick={() => setReview(r => ({ ...r, proof_image_url: '' }))}
                      className="absolute top-2 right-2 w-7 h-7 bg-black/80 border border-[#333] rounded-sm flex items-center justify-center text-zinc-500 hover:text-rm-red transition-colors">
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center border border-dashed border-[#2a2a2a] rounded-sm p-6 cursor-pointer hover:border-rm-red/30 transition-colors bg-[#0a0a0a]"
                    data-testid="proof-upload">
                    <input type="file" accept="image/*" className="hidden" onChange={handleProofUpload} />
                    {proofLoading ? (
                      <div className="w-5 h-5 border-2 border-rm-red border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Plus size={20} className="text-zinc-700 mb-1.5" />
                        <span className="text-xs text-zinc-600">Click to upload screenshot</span>
                      </>
                    )}
                  </label>
                )}
              </div>
              <div className="flex justify-center">
                <ReCAPTCHA ref={captchaRef} sitekey={SITE_KEY} theme="dark" onChange={setCaptchaToken} />
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setReviewModal(false)} className="flex-1 btn-outline py-2.5 text-sm rounded-sm">Cancel</button>
                <button type="submit" disabled={reviewLoading || !captchaToken || !review.proof_image_url} data-testid="review-submit"
                  className="flex-1 btn-primary py-2.5 text-sm rounded-sm disabled:opacity-40">
                  {reviewLoading ? 'Submitting...' : 'Submit Review'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 modal-backdrop z-50 flex items-center justify-center p-4 cursor-zoom-out" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="Proof" className="max-w-full max-h-full rounded-sm shadow-2xl" />
        </div>
      )}
    </div>
  );
}
